import { Button, Illustration, Typography } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { UnauthEntryPoint } from "@shared/hooks/useAnalyticsLogger/UnauthAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useDebounce } from "@shared/hooks/useDebounce/useDebounce";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./MarketplaceCtaBanner.styles";

const CLICK_DEBOUNCE_MS = 500;

interface Props {
  entryPoint: UnauthEntryPoint;
}

const MarketplaceCtaBanner = ({ entryPoint }: Props) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();
  const [isMarketplaceCtaEnabled] = useFlippers("odyssey_patient_signup_marketplace_cta");
  const logAnalyticsEvent = useAnalyticsLogger();

  // Without flushOnUnmount, submitting the email form unmounts this banner and cancels the pending event.
  const handleClick = useDebounce(
    () => {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.UNAUTH_PAGE_BROWSE_LAB_TESTS,
        payload: { entry_point: entryPoint },
      });
    },
    CLICK_DEBOUNCE_MS,
    { flushOnUnmount: true }
  );

  if (!isMarketplaceCtaEnabled) return null;
  // labs.fullscript.com is a US-only property, so there is nowhere to send Canadian visitors.
  if (!isUS) return null;

  return (
    <div css={styles.band}>
      <div css={styles.content}>
        <Illustration
          customCss={styles.illustration}
          illustration="LabResults"
          intention="success"
        />
        <div css={styles.copy}>
          <Typography as="h3" isMarginless variant="h4">
            {t(l.practitionerSignUp.marketplaceCtaBanner.title)}
          </Typography>
          <Typography isMarginless>
            {t(l.practitionerSignUp.marketplaceCtaBanner.description)}
          </Typography>
        </div>
        <Button
          href={authRoutes.labsMarketplace}
          onClick={handleClick}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t(l.practitionerSignUp.marketplaceCtaBanner.cta)}
        </Button>
      </div>
    </div>
  );
};

export { MarketplaceCtaBanner };
